import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createBusinessProfile as createBusinessProfileAPI,
  deleteBusinessProfile as deleteBusinessProfileAPI,
  fetchBusinessProfileById,
  fetchBusinessProfiles,
  updateBusinessProfile as updateBusinessProfileAPI,
} from '@services/business/BusinessProfileService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { BusinessProfile } from '../../models/business/BusinessProfile'
import { businessProfileSchema } from '../../models/schema'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<BusinessProfile> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'businessProfile',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getBusinessProfilesSuccess,
  setQuery: setBusinessProfileQuery,
  reset: resetBusinessProfileState,
  resetQueryAndIds: resetBusinessProfileQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: BusinessProfile }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectBusinessProfiles = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedBusinessProfile = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectBusinessProfileById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectBusinessProfilesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedBusinessProfile =
  (businessProfile: BusinessProfile | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<BusinessProfile>(businessProfile)
      )
    )

export const getBusinessProfiles = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchBusinessProfiles(params),
    [businessProfileSchema],
    slice.actions
  )

export const createBusinessProfile = (businessProfile: BusinessProfile) =>
  SBAPI.CreateDispatched(
    createBusinessProfileAPI(businessProfile),
    businessProfileSchema,
    slice.actions
  )

export const getBusinessProfileWithId = (id: number) =>
  SBAPI.FetchDispatched<BusinessProfile>(
    fetchBusinessProfileById(id),
    businessProfileSchema,
    slice.actions
  )

export const updateBusinessProfile = (businessProfile: BusinessProfile) =>
  SBAPI.UpdateDispatched<BusinessProfile>(
    updateBusinessProfileAPI(businessProfile),
    businessProfile,
    slice.actions
  )

export const deleteBusinessProfile = (businessProfile: BusinessProfile) =>
  SBAPI.DestroyDispatched<BusinessProfile>(
    deleteBusinessProfileAPI(businessProfile),
    businessProfile,
    slice.actions
  )
